<template>
  <div :class="[$options.name, { 'tw:text-center': centered }]" class="tw:h-fit tw:flex tw:flex-col tw:gap-3">
    <div v-if="showProgress" class="tw:mb-4 tw:w-[10rem]">
      <EdroneProgressBar :value="progressValue" type="default" :badge="false" class="progress-bar-transition" />
    </div>
    <div :class="{ 'tw:text-center': centered }">
      <h2 :class="[titleClass, 'tw:m-0 tw:flex tw:items-center', { 'tw:justify-center': centered }]">
        <template v-if="!$slots.title">{{ title }}</template>
        <!-- Use this slot for custom title content or prop "title" -->
        <slot v-if="!title" name="title" />
      </h2>
      <p v-if="desc" :class="[descClass, 'tw:!text-default-700 tw:!m-0 tw:pt-1', { 'tw:text-center': centered }]">
        {{ desc }}
      </p>
    </div>
    <div v-if="hasLinkSlot" :class="`${$options.name}__links tw:heading-2`">
      <!-- @slot DEPRECATED. No longer in Design System. -->
      <slot name="links" />
    </div>
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from "vue";
import EdroneProgressBar from "./EdroneProgressBar.vue";
import { useIsScreenMobile } from "vue-app/composables/useIsScreenMobile/useIsScreenMobile.composable";

export default defineComponent({
  name: "EdroneHeader",
  components: {
    EdroneProgressBar,
  },
  props: {
    /**
     * Title of the header. Use this prop or slot "title" for custom title content.
     */
    title: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Description text displayed below the title.
     */
    desc: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Header size type. Determines the visual hierarchy and font size.
     */
    type: {
      type: String as PropType<"h1" | "h2" | "h3" | "h4">,
      default: "h1",
    },
    /**
     * Optional header size for mobile devices (max-width: 1024px). If set, overrides the `type` prop on mobile.
     */
    mobileType: {
      type: String as PropType<"h1" | "h2" | "h3" | "h4" | null>,
      default: null,
    },
    /**
     * Whether to display a progress bar above the header.
     */
    showProgress: {
      type: Boolean,
      default: false,
    },
    /**
     * Current step number for progress calculation. Used with `totalSteps` to calculate progress percentage.
     */
    currentStep: {
      type: Number,
      default: undefined,
    },
    /**
     * Total number of steps for progress calculation. Used with `currentStep` to calculate progress percentage.
     */
    totalSteps: {
      type: Number,
      default: undefined,
    },
    /**
     * Whether to center-align the header content.
     */
    centered: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { isMobile } = useIsScreenMobile();

    return {
      isMobile,
    };
  },
  computed: {
    hasLinkSlot(): boolean {
      return !!this.$slots.links;
    },
    progressValue(): number | undefined {
      if (!this.currentStep || !this.totalSteps) {
        return undefined;
      }
      return Math.round((this.currentStep / this.totalSteps) * 100);
    },
    titleClass() {
      const effectiveType = this.isMobile && this.$props.mobileType ? this.$props.mobileType : this.$props.type;
      switch (effectiveType) {
        case "h1":
          return "tw:heading-1";
        case "h2":
          return "tw:heading-2";
        case "h3":
          return "tw:heading-3";
        case "h4":
          return "tw:body-2--bold";
        default:
          return "tw:heading-1";
      }
    },
    descClass() {
      const effectiveType = this.isMobile && this.$props.mobileType ? this.$props.mobileType : this.$props.type;
      switch (effectiveType) {
        case "h1":
          return "tw:body-1";
        case "h2":
          return "tw:body-2";
        case "h3":
          return "tw:body-2";
        case "h4":
          return "tw:caption";
        default:
          return "tw:body-1";
      }
    },
  },
});
</script>

<style scoped>
.progress-bar-transition :deep(.EdroneProgressBar__bar) {
  transition: width 400ms ease-out;
}
</style>
